import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Sparkles, Users, Star, CalendarClock, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, StatusBadge, CandidateAvatar } from "@/components/Common";
import { JobMeta } from "@/components/JobForm";
import { InterviewDialog } from "@/components/RequestDialogs";
import { OfferDialog } from "@/components/OfferDialog";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr, fmtDate, useFetch } from "@/lib/api";
import { isIOSApp, originUrl, openExternal } from "@/lib/native";

const FeatureCard = ({ j }) => {
  const [busy, setBusy] = useState(false);
  const { data: price } = useFetch("/featured-job/price");
  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${j.id}/feature`, { origin_url: originUrl() });
      await openExternal(data.checkout_url);
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };
  if (j.featured) return <p data-testid="job-featured-note" className="mt-4 flex items-center gap-1.5 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800"><Star className="h-4 w-4 fill-amber-500 text-amber-500" />Featured until {fmtDate(j.featured_until)}</p>;
  if (!["Pending", "Approved"].includes(j.status) || isIOSApp) return null;
  return (
    <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-4">
      <p className="flex items-center gap-1.5 text-sm font-medium text-amber-900"><Star className="h-4 w-4" />Feature this ad</p>
      <p className="mt-1 text-xs text-amber-800">Pin it to the top of the candidate job board for {price?.days ?? 30} days.</p>
      <Button size="sm" data-testid="feature-job-button" disabled={busy} onClick={buy} className="mt-3 bg-amber-600 hover:bg-amber-700">
        {busy ? "Opening checkout…" : `Feature for $${price?.price ?? 49} ${(price?.currency || "aud").toUpperCase()}`}
      </Button>
    </div>
  );
};

const HireActions = ({ c, id }) => {
  const [iv, setIv] = useState(false);
  const [of, setOf] = useState(false);
  return (
    <>
      <Button size="sm" variant="outline" data-testid={`interview-applicant-${id}`} onClick={() => setIv(true)}><CalendarClock className="mr-1 h-3.5 w-3.5" />Interview</Button>
      <Button size="sm" data-testid={`offer-applicant-${id}`} onClick={() => setOf(true)} className="bg-amber-600 hover:bg-amber-700"><Send className="mr-1 h-3.5 w-3.5" />Send offer</Button>
      <InterviewDialog candidate={c} open={iv} onOpenChange={setIv} />
      <OfferDialog candidate={c} open={of} onOpenChange={setOf} />
    </>
  );
};

const CandRow = ({ c, base, children, testId }) => (
  <div data-testid={testId} className="flex flex-wrap items-center gap-4 rounded-md border border-stone-200 bg-white p-4">
    <CandidateAvatar candidate={c} className="h-11 w-11" />
    <div className="min-w-0 flex-1">
      <Link to={`${base}/${c.id}`} className="font-medium text-stone-900 hover:underline">{c.name}</Link>
      <p className="text-xs text-stone-500">{c.title} · {c.experience_years} yrs · ${c.hourly_rate}/hr</p>
    </div>
    {children}
  </div>
);

const SuggestDialog = ({ open, onOpenChange, jobId, onDone }) => {
  const { data: cands } = useFetch("/candidates?status=Available");
  const [cid, setCid] = useState("");
  const [note, setNote] = useState("");
  const send = async () => {
    try {
      onDone((await api.post(`/admin/jobs/${jobId}/suggest`, { candidate_id: cid, note })).data);
      toast.success("Candidate suggested to the business");
      onOpenChange(false);
      setCid(""); setNote("");
    } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="suggest-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Suggest a candidate</DialogTitle><DialogDescription>The business gets an email and sees your suggestion on their job.</DialogDescription></DialogHeader>
        <Select value={cid} onValueChange={setCid}>
          <SelectTrigger data-testid="suggest-candidate-select"><SelectValue placeholder="Choose a candidate" /></SelectTrigger>
          <SelectContent>{(cands || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.title}</SelectItem>)}</SelectContent>
        </Select>
        <Textarea data-testid="suggest-note-input" rows={3} placeholder="Why they're a great fit" value={note} onChange={(e) => setNote(e.target.value)} />
        <DialogFooter><Button data-testid="suggest-submit-button" disabled={!cid} onClick={send}>Send suggestion</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StatsPanel = ({ s, featured }) => {
  const items = [["Candidate views", s.views, "stat-views"], ["Applicants", s.applicants, "stat-applicants"], ["View → apply", `${s.conversion}%`, "stat-conversion"]];
  if (s.views_featured != null) items.push(["Views while featured", s.views_featured, "stat-views-featured"], ["Applicants while featured", s.applicants_featured, "stat-applicants-featured"]);
  return (
    <section data-testid="job-stats" className="mb-6 rounded-md border border-stone-200 bg-white p-5">
      <p className="mb-3 flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-stone-500">{featured && <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />}Ad performance</p>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
        {items.map(([l, v, t]) => <div key={t} data-testid={t}><p className="font-display text-3xl font-extrabold text-stone-900">{v}</p><p className="text-xs text-stone-500">{l}</p></div>)}
      </div>
    </section>
  );
};

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const base = admin ? "/admin/candidates" : "/app/candidates";
  const { data: j, setData, reload } = useFetch(`/jobs/${id}`, [id]);
  const [suggest, setSuggest] = useState(false);

  const moderate = async (status) => {
    try { await api.patch(`/admin/jobs/${id}`, { status, admin_note: "" }); reload(); toast.success(`Job ${status.toLowerCase()}`); } catch (e) { toast.error(formatErr(e)); }
  };
  const close = async () => { try { await api.post(`/jobs/${id}/close`); reload(); } catch (e) { toast.error(formatErr(e)); } };
  const setApp = async (a, status) => {
    try {
      const { data } = await api.patch(`/job-applications/${a.id}`, { status });
      setData((p) => ({ ...p, applications: p.applications.map((x) => (x.id === a.id ? { ...x, ...data } : x)) }));
    } catch (e) { toast.error(formatErr(e)); }
  };
  if (!j) return <p className="text-stone-500">Loading…</p>;

  return (
    <div data-testid="job-detail">
      <Link to={admin ? "/admin/jobs" : "/app/jobs"} className="mb-6 inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-900"><ArrowLeft className="h-4 w-4" />All jobs</Link>
      <PageHeader eyebrow={admin ? j.business?.name : "Job ad"} title={j.title}>
        <StatusBadge status={j.status} testId="job-detail-status" />
        {admin && j.status !== "Approved" && <Button size="sm" data-testid="approve-job-button" onClick={() => moderate("Approved")} className="bg-emerald-700 hover:bg-emerald-800">Approve</Button>}
        {admin && j.status === "Pending" && <Button size="sm" variant="outline" data-testid="reject-job-button" onClick={() => moderate("Rejected")}>Reject</Button>}
        {!admin && j.status !== "Closed" && <Button size="sm" variant="outline" data-testid="close-job-button" onClick={close}>Close job</Button>}
      </PageHeader>
      {j.stats && <StatsPanel s={j.stats} featured={j.featured} />}
      <div className="grid gap-6 lg:grid-cols-3">
        <section className="rounded-md border border-stone-200 bg-white p-6 lg:col-span-1">
          <JobMeta j={j} />
          <p className="mt-4 whitespace-pre-wrap text-sm text-stone-700">{j.description}</p>
          {j.requirements && <><p className="mt-4 text-[11px] font-mono uppercase text-stone-500">Requirements</p><p className="mt-1 whitespace-pre-wrap text-sm text-stone-700">{j.requirements}</p></>}
          {j.admin_note && <p className="mt-4 text-sm italic text-stone-500">Team note: {j.admin_note}</p>}
          {admin && j.featured && <p className="mt-4 text-sm text-amber-700">Featured until {fmtDate(j.featured_until)}</p>}
          {admin && j.alerts_sent != null && <p className="mt-2 text-xs text-stone-500" data-testid="job-alerts-sent">Job alert emails sent to {j.alerts_sent} matching candidates</p>}
          {!admin && <FeatureCard j={j} />}
        </section>
        <div className="space-y-6 lg:col-span-2">
          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-display text-xl font-bold"><Sparkles className="h-5 w-5 text-amber-600" />Suggested by our team</h2>
              {admin && <Button size="sm" data-testid="suggest-candidate-button" onClick={() => setSuggest(true)}>Suggest a candidate</Button>}
            </div>
            {j.suggestions.length === 0 && <p className="text-sm text-stone-500">No suggestions yet.</p>}
            <div className="space-y-2">{j.suggestions.map((s) => (
              <CandRow key={s.id} c={s.candidate} base={base} testId={`suggestion-${s.id}`}>
                {s.note && <p className="w-full text-sm italic text-stone-600 sm:w-auto">"{s.note}"</p>}
                {!admin && <HireActions c={s.candidate} id={s.id} />}
              </CandRow>
            ))}</div>
          </section>
          <section>
            <h2 className="mb-3 flex items-center gap-2 font-display text-xl font-bold"><Users className="h-5 w-5" />Applicants ({j.applications.length})</h2>
            {j.applications.length === 0 && <p className="text-sm text-stone-500">No applicants yet.</p>}
            <div className="space-y-2">{j.applications.map((a) => (
              <CandRow key={a.id} c={a.candidate} base={base} testId={`applicant-${a.id}`}>
                <span className="text-[11px] font-mono text-stone-400">{fmtDate(a.created_at)}</span>
                <StatusBadge status={a.status} />
                {!admin && <HireActions c={a.candidate} id={a.id} />}
                <Button size="sm" variant="outline" data-testid={`shortlist-applicant-${a.id}`} onClick={() => setApp(a, "Shortlisted")}>Shortlist</Button>
                <Button size="sm" variant="ghost" data-testid={`reject-applicant-${a.id}`} onClick={() => setApp(a, "Rejected")} className="text-red-600">Reject</Button>
                {a.cover_note && <p className="w-full text-sm text-stone-600">"{a.cover_note}"</p>}
              </CandRow>
            ))}</div>
          </section>
        </div>
      </div>
      {admin && <SuggestDialog open={suggest} onOpenChange={setSuggest} jobId={id} onDone={(s) => setData((p) => ({ ...p, suggestions: [s, ...p.suggestions] }))} />}
    </div>
  );
}
